import React, { useState, useEffect } from 'react';
import { logo } from '../assets/svg/svg';
import { useNavigate } from 'react-router-dom';
import { getUserContract } from '../helper/contract-helper';
import Cookies from "universal-cookie";
import { Bounce, toast } from 'react-toastify';
import { getSymmetricKey, login } from '../service/api-actions.js';
import { buildLoginProof } from '../auth/loginProof.js';
import { contactDB, deleteDatabase, deleteContacts } from '../db/db-helper.js';
import { getFirstLetterUppercase } from '../helper/object-validation-helper.js';
import { CompleteLoader } from '../modals/common-alert.js';
import TryAgainModal from '../modals/TryAgainModal.js';
import { getContacts, getPublicKeyValue } from '../helper/web3helper.js';
import { transactionAction } from '../helper/chain-helper.js';
import { getAssetWalletAddress } from '../helper/wallet-helper.js';
import { reportError, toErrorString } from '../utils/errorLogger.js';
import { clearContactsDekCache, primeContactsDek } from "../helper/contactsDek";
import { clearDekCache } from '../helper/dekManager.js';
import { clearEnvelopeKeyCache } from '../helper/envelopeV3.js';
import { getCacheOwner } from '../db/indexed-helper.js';
const cookies = new Cookies();

// userContract's Attribute[] is a tuple[] of (string key, string value). Signup writes it as an array of
// [key, value] PAIRS (routes/Signup.js), which is what the encoder expects; but the same attributes read
// back off-chain via getUserDetailsForWallet arrive as web3 Result objects ({ 0, 1, key, value }). This
// normalises whatever shape we were handed back to the pair form used on the wire, so a legacy row can be
// re-sent without losing or reordering any attribute the account already carries.
const toAttributePairs = (attributes) =>
    (Array.isArray(attributes) ? attributes : []).map((attribute) => {
        if (Array.isArray(attribute)) return [attribute[0], attribute[1]];
        return [attribute?.key ?? attribute?.[0], attribute?.value ?? attribute?.[1]];
    });

const SignIn = () => {

    const checkAuth = () => {

        let walletAddress = localStorage.getItem("walletAddress")
        localStorage.clear();
        sessionStorage.clear();
        cookies.remove("userObject");
        cookies.remove("accessToken");
        localStorage.setItem("walletAddress", walletAddress)
    }

    useEffect(() => {
        checkAuth();
    }, [])


    const history = useNavigate();
    const [text, setText] = useState("")
    const [loader, setLoader] = useState(false);
    const [walletUsers, setWalletUsers] = useState([]);
    const [showRetryModal, setShowRetryModal] = React.useState(false);
    const [retryUserData, setRetryUserData] = useState(null);

    useEffect(() => {

        const fetchWalletAccounts = async () => {
            setLoader(true)
            await new Promise((resolve) => setTimeout(resolve, 1000));
            if (!window.ncogProvider) {
                history('/'); // Redirect to home if provider is not available
                return;
            }
            const walletAddress = localStorage.getItem("walletAddress");
            if (!walletAddress) {
                history('/');
                return;
            }
            if (walletAddress) {
                try {
                    const contract = getUserContract();
                    if (walletAddress) {
                        const getUserDetailsForWallet = await contract.methods.getUserDetailsForWallet(walletAddress).call();
                        if (getUserDetailsForWallet.length) {
                            setWalletUsers(getUserDetailsForWallet);
                        } else {
                            setWalletUsers([]);
                        }
                    }
                } catch (err) {
                    console.log(err);
                }
            } else {
                history('/');
            }
            setLoader(false)
        }
        fetchWalletAccounts();
    }, [history])

    const handleOnSelectAccount = async (userData) => {
        setLoader(true);
        setText("Please wait, data is loading from the blockchain, Going forward it will be fast.")
        try {
            if (userData) {
                if (userData && !userData.publicKey) {
                    const publicKey = await getPublicKeyValue(userData?.userName);
                    const userContract = getUserContract();
                    // Legacy rows predate BOTH the publicKey and the "assetAddress" attribute, so this repair
                    // must backfill the attribute too. Login proof-of-possession binds the challenge signature
                    // to keccak256(signerPublicKey)[12:] == the account's on-chain assetAddress; repairing only
                    // publicKey would write a row that can NEVER sign in, in this or any DSuite app — and since
                    // the userName is already taken the user could not re-sign-up either. Do not "simplify" this
                    // back to passing userData.attributes through verbatim.
                    const assetAddress = await getAssetWalletAddress();
                    if (!assetAddress) {
                        // Abort BEFORE sending: a repair transaction without the attribute is a permanent
                        // lockout, whereas not sending one leaves the account exactly as repairable as it was.
                        toast.error("Could not read your wallet's asset address. Please reconnect your NCOG wallet and try again.", {
                            position: "top-center",
                            transition: Bounce,
                        });
                        return false;
                    }
                    // Preserve every existing attribute; only add assetAddress when the row does not already
                    // carry one (never duplicate the key — the backend reads the first match).
                    const attributes = toAttributePairs(userData?.attributes);
                    if (!attributes.some(([key]) => key === "assetAddress")) {
                        attributes.push(["assetAddress", assetAddress]);
                    }
                    const updateUserParams = [userData?.walletAddress, userData?.userName, publicKey, userData?.organizationId, userData?.role, userData?.name, true, attributes];
                    const hash = await transactionAction(userContract, 'updateUserAccount', updateUserParams, userData?.walletAddress, 'userContract', null, true);
                    if (hash) {
                        userData.publicKey = publicKey;
                    }
                }
                // Wallet proof-of-possession, ALWAYS. buildLoginProof fetches a challenge and signs it
                // with the wallet's ML-DSA key; login() then sends signature + signerPublicKey alongside
                // the identity. If the proof step fails it throws a user-facing message, which we reshape
                // into the same isAuth:false result login() would return so the error branch below still
                // surfaces it. There is no unsigned fallback by design.
                let getToken;
                try {
                    const { signature, signerPublicKey } = await buildLoginProof(userData.userName, userData.publicKey);
                    getToken = await login(userData.userName, userData.publicKey, null, signature, signerPublicKey);
                } catch (proofError) {
                    getToken = { isAuth: false, isAccess: false, message: proofError.message };
                }
                if (getToken && getToken.isAuth && getToken.token) {
                    localStorage.setItem('UserName', userData.userName);
                    cookies.set("userObject", '', { path: "/", maxAge: 0 });
                    const userObject = { name: userData.userName, wallet: userData.walletAddress, publicKey: userData.publicKey };
                    cookies.set("accessToken", getToken?.token, { path: "/" });
                    localStorage.setItem("accessToken", getToken?.token);
                    cookies.set("userObject", JSON.stringify(userObject), { path: "/" });
                    localStorage.setItem("userObject", JSON.stringify(userObject));

                    // Keep this account's mail cache across sign-ins.
                    //
                    // This used to be an unconditional deleteDatabase() further down: every sign-in threw
                    // away every message the user had already decrypted, so the wallet had to re-decrypt
                    // the entire mailbox next session — one prompt per message. The cache is now encrypted
                    // at rest under the dMail DEK (db/indexed-helper.js), which is what makes keeping it
                    // safe: what survives on disk is ciphertext only the wallet can open.
                    //
                    // The wipe still runs whenever the cache cannot be proven to belong to the account
                    // signing in — a real account switch, or a pre-owner-tag cache whose owner is unknown
                    // (null). Mail ids are unique only per user, so serving another account's rows would
                    // show the wrong mail. The in-memory DEKs are dropped with it, so the incoming account
                    // never inherits the previous account's keys. Ordered before primeContactsDek() below
                    // so the clear cannot discard the key that sign-in just unwrapped.
                    const cacheOwner = await getCacheOwner();
                    if (cacheOwner !== userData.userName) {
                        clearDekCache();
                        clearContactsDekCache();
                        // And the v3 per-message content keys, which were unwrapped by the previous
                        // account's wallet.
                        clearEnvelopeKeyCache();
                        await deleteDatabase();
                        // deleteDatabase() only drops 'web3Dmails'. The contacts cache lives in a
                        // SEPARATE 'Dcontact' DB whose rows are keyed by the constant "contactList"
                        // with no userName tag — so without this the previous account's decrypted
                        // contacts survive the switch and get merged into the incoming user's list
                        // (and their uuids mark the real contacts as already-decrypted, hiding them).
                        await deleteContacts();
                    }

                    const symmetricKey = await getSymmetricKey({ userName: userData?.userName });
                    let decryptedResponse;
                    if (symmetricKey?.data?.encrypted_key) {
                        decryptedResponse = await window.ncogProvider.request({
                            method: "ncog_decrypt",
                            params: { cypherText: symmetricKey?.data?.encrypted_key, version: 'v1' },
                        });
                        if (decryptedResponse?.response && decryptedResponse?.response?.success != false) {
                            setShowRetryModal(false);
                            setRetryUserData(null);
                        } else {
                            setRetryUserData(userData);
                            setShowRetryModal(true);
                            return false;
                        }
                        // Seed the in-memory cache instead of persisting the key. This unwrap is the
                        // session's only one — every later reader (getContacts, Profile, ...) reuses
                        // it. The DEK is deliberately NOT written to IndexedDB: it used to be, was
                        // never read back, and was never deleted on logout.
                        primeContactsDek(userData?.userName, decryptedResponse?.response);
                    }
                    getContacts(userData.userName, symmetricKey?.data?.encrypted_key !== null ? decryptedResponse?.response : null);
                    history('/inbox');


                    // Optional chaining on BOTH reads: a proof-of-possession rejection carries no isAccess
                    // field at all, and login() can resolve to null — `!getToken.isAuth` would then throw and
                    // land in the outer catch, swallowing the message instead of showing it.
                } else if (!getToken?.isAccess && !getToken?.isAuth) {
                    toast.error(getToken?.message, {
                        position: "top-center",
                        transition: Bounce,
                        autoClose: 5000,
                    });
                } else {
                    toast.error("Something went wrong", {
                        position: "top-center",
                        transition: Bounce,
                    });
                    window.open('/app/signup', '_self');
                    if (!toast.isActive("wentWrongErro")) {
                        toast.error("Something went wrong", {
                            toastId: "wentWrongErro",
                            position: "top-center",
                            transition: Bounce,
                        });
                    }
                }
            }
        } catch (error) {
            console.log('error', error);
            const errorStr = toErrorString(error || error.message);
            const userName = localStorage.getItem('UserName');
            reportError({
                endpoint: '-',
                message: `FileName: Signin.js, Fun: handleOnSelectAccount, ${String(error.message || 'Unhandled error')}`,
                error: errorStr,
                userName: userName || '',
            });
        } finally {
            setLoader(false);
            setText("")
        }
    }

    return (
        <div className="signin-container">
            <div className="signin-card">
                <div className="signin-logo-container">
                    {logo.dmail_logo_svg}
                    <h4 className="signin-logo-container-h4"> DMail </h4>
                </div>
                <h2 className="signin-title">
                    {`${!walletUsers?.length ? "Create an account" : "Choose an account"}`}
                </h2>
                <p className="signin-subtitle">
                    to continue to <span className="domain-input-default">DMail</span>
                </p>

                <div className="signin-accounts-list">
                    <div className="signin-sidebar-menu">
                        {walletUsers?.map((user, index) => (
                            <button
                                key={user.id || `${user.walletAddress}-${user.userName}-${index}`}
                                className="signin-account-button"
                                onClick={() => handleOnSelectAccount(user)}
                            >
                                <div className="signin-account-info">
                                    <div className="signin-avatar">
                                        <div className="user-profile-pic">{getFirstLetterUppercase(user.name.split("@")[0])}</div>
                                    </div>
                                    <div className="signin-user-details">
                                        <p className="signin-user-name">{user.name}</p>
                                        <p className="signin-username">{user.userName}</p>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                    <div>
                        <button
                            onClick={() => window.open("/app/signup", '_self')}
                            className="signin-new-account-button"
                        >
                            <div className="signin-new-account-content">
                                <div className="signin-plus-icon">+</div>
                                <p className="signin-new-account-text">
                                    {`${walletUsers?.length ? "Use another account" : "Create new account"}`}
                                </p>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
            {loader && <CompleteLoader isOpen={loader} text={text} />}
            <TryAgainModal
                isOpen={showRetryModal}
                onRetry={() => {
                    setShowRetryModal(false);
                    handleOnSelectAccount(retryUserData);
                }}
                onCancel={() => {
                    setShowRetryModal(false);
                    setRetryUserData(null);
                }}
            />
        </div>
    )
}

export default SignIn;
